import { FastBackwardFilled, FastForwardFilled, StepBackwardFilled, StepForwardFilled } from "@ant-design/icons";
import { Button, Checkbox, Drawer, InputNumber, Slider, Tooltip } from "antd";
import SessionTimeline from "components/session/SessionTimeline";
import { getRoutePosAtTimestamp } from "components/util/GeometryUtilsV2";
import RouteCoordinateSystems from "components/util/RouteCoordinateSystems";
import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { routeGeomSelector } from "redux/selectors";
import {
    flipInspectionRails,
    getSessionContinuousObservations,
    setAutoScrollActive,
    setSecondaryManualOffset,
    toggleRailInspectionTimelineWidget,
} from "../../../redux/actions/index";
import { calculateElrGroups } from "../../util/Geometry";
import { convertToTimezone } from "../../util/TimezoneUtils";

const userConfigSelector = (state) => state.userDetails.userConfig;
const currentOffsetSelector = (state) => state.playlist.position.currentTimeOffset;
const elrUnitsSelector = (state) => state.userDetails.userConfig.elr_units;
const dashboardsSelector = (state) => state.dashboards;
const userDetailsSelector = (state) => state.userDetails;
const railDataObjectSelector = (state) => state.railInspection.railInspectionImages.data;
const railDataLoadedSelector = (state) => state.railInspection.railInspectionImages.loadingInfo.loaded;
const selectedRailImageSelector = (state) => state.railInspection.selectedRailInspectionImage;
const railsFlippedSelector = (state) => state.railInspection.flipRails;
const routeSystemIDSelector = (state) => state.playlist.data.system_id;
const timelineWidgetOpenSelector = (state) => state.railInspection.timelineWidget.open;
const currentSessionSelector = (state) => state.sessions[state.playlist.data.routeID];
const continuousObservationsSelector = (state) => state.continuousObservations;
const autoScrollActiveSelector = (state) => state.railInspection.autoScrollActive;
const railImageConfigObjectSelector = (state) => state.railInspection.railInspectionImageConfig;
const detectionsExportOpenSelector = (state) => state.railInspection.detectionsExportOpen;
const detectionsExportIndexRangeSelector = (state) => state.railInspection.detectionsExportIndexRange;
const secondaryManualOffsetSelector = (state) => state.railInspection.secondaryManualOffset;

const RailInspectControls = ({ imageIndexChanged, extraStyles, inPlayer, videoless, secondaryRailImages, newRailIndex }) => {
    const dispatch = useDispatch();

    const userConfig = useSelector(userConfigSelector);
    const currentOffset = useSelector(currentOffsetSelector);
    const routeGeometry = useSelector(routeGeomSelector);
    const elrUnits = useSelector(elrUnitsSelector);
    const dashboards = useSelector(dashboardsSelector);
    const userDetails = useSelector(userDetailsSelector);
    const railData = useSelector(railDataObjectSelector);
    const railDataLoaded = useSelector(railDataLoadedSelector);
    const selectedRailImage = useSelector(selectedRailImageSelector);
    const railsFlippedValue = useSelector(railsFlippedSelector);
    const routeSystemID = useSelector(routeSystemIDSelector);
    const timelineWidgetOpen = useSelector(timelineWidgetOpenSelector);
    const currentSession = useSelector(currentSessionSelector);
    const continuousObservations = useSelector(continuousObservationsSelector);
    const autoScrollActive = useSelector(autoScrollActiveSelector);
    const railImageConfigObject = useSelector(railImageConfigObjectSelector);
    const detectionsExportOpen = useSelector(detectionsExportOpenSelector);
    const detectionsExportIndexRange = useSelector(detectionsExportIndexRangeSelector);
    const manualOffset = useSelector(secondaryManualOffsetSelector);

    const [sliderIndex, setSliderIndex] = useState(selectedRailImage.index || newRailIndex);

    const dashboardID = userDetails.dashboardAccessID;
    const currentDashboard = _.find(dashboards, (dash) => dash.access_id === dashboardID);

    const debouncedImageIndexChanged = useCallback(
        _.debounce((index) => {
            imageIndexChanged(index);
        }, 75),
        [],
    );

    useEffect(() => {
        if (selectedRailImage.index !== sliderIndex) {
            debouncedImageIndexChanged(sliderIndex);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imageIndexChanged, sliderIndex]);

    useEffect(() => {
        if (selectedRailImage.index !== sliderIndex) {
            setSliderIndex(selectedRailImage.index);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRailImage]);

    const elrIndexes = useMemo(() => {
        const routePositionData = _.get(routeGeometry, ["distance_to_route_position"], []);
        if (railData && railData.length && routePositionData.length && railDataLoaded) {
            return calculateElrGroups(railData, routeGeometry);
        }
        return {};
    }, [routeGeometry, railData, railDataLoaded]);

    const setManualOffset = useCallback(
        (adjustment) => {
            dispatch(setSecondaryManualOffset(adjustment));
        },
        [dispatch],
    );

    useEffect(() => {
        if (currentSession && currentSession.id) {
            dispatch(getSessionContinuousObservations(currentSession.id));
        }
    }, [currentSession, dispatch]);

    const sessionHasAnalyticsData = useMemo(() => {
        // currently we are only showing contaminated observations in the rail inspection
        return _.filter(continuousObservations, (observation) => observation.class === "contaminated").length ? true : false;
    }, [continuousObservations]);

    const allowRailFlip = useMemo(() => {
        return railImageConfigObject.horizontal;
    }, [railImageConfigObject.horizontal]);

    const getPositionForImage = useCallback(
        (railImage, removeLabel) => {
            if (!routeGeometry) {
                return null;
            }
            const system = _.find(RouteCoordinateSystems, (system) => {
                return system.ID === routeSystemID;
            });

            let positionExtractor;
            if (_.get(railImage, ["mwv", "elr"])) {
                positionExtractor = (railImage) => {
                    return {
                        elr: _.get(railImage, ["mwv", "elr"]),
                        position: parseInt(railImage.mwv.mile) * 80 + parseFloat(railImage.mwv.yard) / 22,
                    };
                };
            }

            if (_.get(railImage, ["position", "elr"])) {
                positionExtractor = (railImage) => {
                    return {
                        elr: railImage.position.elr,
                        position: railImage.position.position,
                    };
                };
            } else {
                positionExtractor = (railImage) => {
                    const routePosition = getRoutePosAtTimestamp(railImage.timestamp / 1000, routeGeometry, routeSystemID);
                    if (!routePosition) {
                        return null;
                    }
                    return {
                        elr: routePosition.route,
                        position: routePosition.position,
                    };
                };
            }

            const posData = positionExtractor(railImage);
            if (!posData) {
                return null;
            }
            const calculatedELR = system.create(posData.elr, posData.position);
            if (calculatedELR) {
                if (calculatedELR && calculatedELR.to_simple_string) {
                    const elr = calculatedELR.to_simple_string(elrUnits, [], removeLabel);
                    return elr;
                } else if (calculatedELR && calculatedELR.to_string) {
                    const elr = calculatedELR.to_string(elrUnits, [], removeLabel);
                    return elr;
                }
            }
            return null;
        },
        [elrUnits, routeGeometry, routeSystemID],
    );

    const elrLabels = useMemo(() => {
        if (inPlayer) {
            return {};
        }
        if (railData && railData.length && railDataLoaded) {
            let marks = {};
            const groupRatio = railData.length / 55;
            const heightRatio = railData.length / 20;

            let lastELR;
            let elrGroups = {};
            let elrArray = [];
            let lastPos = 0;

            for (let i = 0; i < elrIndexes.length; i++) {
                const elr = elrIndexes[i].elr;
                const index = elrIndexes[i].start;
                if (lastELR && lastELR !== elr) {
                    const difference = index - lastPos;

                    if (difference < groupRatio) {
                        elrArray.push(elr);
                    } else {
                        if (elrArray.length) {
                            elrGroups[lastPos] = elrArray;
                        }
                        elrArray = [elr];
                    }
                    lastPos = index;
                }
                lastELR = elr;
            }

            console.log("debug elrArray", elrArray);
            if (elrArray.length) {
                elrGroups[lastPos] = elrArray;
            }

            console.log("debug elrGroups", elrGroups);

            let marginTop = "-34px";
            let lastMarginTop = 0;
            let lastMark = 0;
            let lastDifference = 0;

            const keys = Object.keys(elrGroups);
            for (let i = 0; i < keys.length; i++) {
                let content = elrGroups[keys[i]];
                let markIndex = parseInt(keys[i]);

                let element;

                if (content.length > 1) {
                    let values = content.map((i) => {
                        return <span className="inspectRail-Controls__Inner__Label__Elr">{i}</span>;
                    });

                    element = (
                        <Tooltip
                            title={values}
                            placement="top">
                            <div className="inspectRail-Controls__Inner__Label">
                                <span>{content.length}</span>
                            </div>
                        </Tooltip>
                    );
                } else {
                    element = (
                        <div className="inspectRail-Controls__Inner__Label">
                            <strong>{content}</strong>
                        </div>
                    );
                }

                let difference = markIndex - lastMark;

                if (lastMark > 0) {
                    if (difference < heightRatio) {
                        marginTop = "-50px";
                        if (lastDifference < heightRatio) {
                            if (lastMarginTop === -"34px") {
                                marginTop = "-50px";
                            } else {
                                marginTop = "-34px";
                            }
                        }
                    } else {
                        marginTop = "-34px";
                    }
                }

                lastMark = markIndex;
                lastDifference = difference;
                lastMarginTop = marginTop;

                marks[Object.keys(elrGroups)[i]] = {
                    style: { color: "#a9c447", fontSize: "0.7vw", marginTop: marginTop },
                    label: element,
                };
            }

            const interval = Math.floor(railData.length / 5);

            for (let i = 0; i <= 5; i++) {
                let index = interval * i;
                let scrollerIndex = interval * i;
                if (i === 5) {
                    index -= 1;
                }
                if (scrollerIndex === railData.length) {
                    scrollerIndex -= 1;
                }

                if (railData[index]) {
                    let removeLabel = true;

                    if (i === 0 || i === 5) {
                        removeLabel = false;
                    }

                    const elr = getPositionForImage(railData[index], removeLabel);
                    if (elr) {
                        marks[scrollerIndex] = {
                            style: { color: "#b09aee", fontSize: 12 },
                            label: <strong>{elr}</strong>,
                        };
                    }
                }
            }

            if (detectionsExportOpen && !_.isNil(detectionsExportIndexRange)) {
                let startIndex = detectionsExportIndexRange.start || 0;
                if (marks.hasOwnProperty(startIndex)) {
                    startIndex += 1;
                }
                marks[startIndex] = {
                    label: <span className="inspectRail-Controls__ExportRangeMarker"></span>,
                };

                let endIndex = detectionsExportIndexRange.end || railData.length - 1;
                if (marks.hasOwnProperty(endIndex)) {
                    endIndex -= 1;
                }
                marks[endIndex] = {
                    label: <span className="inspectRail-Controls__ExportRangeMarker"></span>,
                };
            }

            return marks;
        }
    }, [inPlayer, railData, railDataLoaded, detectionsExportOpen, detectionsExportIndexRange, elrIndexes, getPositionForImage]);

    const moveIndex = (indexesToMove) => {
        const newIndex = newRailIndex + indexesToMove;
        imageIndexChanged(newIndex);
    };

    const formatTime = useCallback(
        (sliderIndex, offset) => {
            let displayTime = 0;
            let timeTooltip = "Unknown";

            if (railData[sliderIndex]) {
                displayTime = railData[sliderIndex].timestamp / 1000;
            }

            if (displayTime > 0) {
                const dpDate = new Date(displayTime * 1000);
                const date = convertToTimezone(dpDate, userConfig.convert_to_utc);

                const nd = <span>{date.split(" ")[0]}</span>;
                const nt = <span>{date.split(" ")[1]}</span>;
                const tz = <span>{date.split(" ")[2]}</span>;

                timeTooltip = (
                    <>
                        {nd}
                        {nt}
                        {tz}
                    </>
                );
            }

            return timeTooltip;
        },
        [userConfig, railData],
    );

    const formattedTime = useMemo(() => {
        if (railData) {
            return formatTime(sliderIndex, currentOffset);
        }
    }, [railData, formatTime, sliderIndex, currentOffset]);

    const setSecondaryAdjustment = useCallback(
        (amount) => {
            if (autoScrollActive) {
                dispatch(setAutoScrollActive(false));
            }

            setManualOffset(amount);
        },
        [autoScrollActive, dispatch, setManualOffset],
    );

    return (
        <div
            className="inspectRail-Controls"
            style={extraStyles}>
            <div className="inspectRail-Controls__Outer">
                <div className="inspectRail-Controls__Outer__Icons">
                    <div>
                        <FastBackwardFilled
                            id="railInspectionFastBackward"
                            onClick={() => moveIndex(-5)}
                        />
                    </div>
                    <div>
                        <StepBackwardFilled
                            id="railInspectionStepBackward"
                            onClick={() => moveIndex(-1)}
                        />
                    </div>
                    <div>
                        <StepForwardFilled
                            id="railInspectionStepForward"
                            onClick={() => moveIndex(1)}
                        />
                    </div>
                    <div>
                        <FastForwardFilled
                            id="railInspectionFastForward"
                            onClick={() => moveIndex(5)}
                        />
                    </div>
                </div>
            </div>
            <div className="inspectRail-Controls__Inner">
                <div className="inspectRail-Controls__Inner__Time">{formattedTime}</div>
                <div className="inspectRail-Controls__Inner__Slider">
                    <Slider
                        min={0}
                        max={railData.length - 1}
                        value={sliderIndex}
                        onChange={setSliderIndex}
                        tipFormatter={null}
                        marks={elrLabels}
                        included={false}
                        step={1}
                        className={"Image-Step-Slider"}
                    />
                </div>
            </div>
            <div className="inspectRail-Controls__Outer right">
                {inPlayer && videoless && allowRailFlip && (
                    <Checkbox
                        onChange={(e) => {
                            dispatch(flipInspectionRails(e.target.checked));
                            if (autoScrollActive) {
                                dispatch(setAutoScrollActive(false));
                            }
                        }}
                        checked={railsFlippedValue}>
                        Flip Rails
                    </Checkbox>
                )}
                {currentDashboard && secondaryRailImages && (
                    <div className="inspectRail-Controls__Outer_RailAdjustment">
                        <label>Adjust Alignment</label>
                        <InputNumber
                            onChange={setSecondaryAdjustment}
                            min={-500000}
                            max={500000}
                            precision={2}
                            step={0.1}
                            value={manualOffset}
                        />
                    </div>
                )}
            </div>

            {userDetails?.userConfig?.super_admin && !inPlayer && sessionHasAnalyticsData && (
                <div className="inspectRail-Controls__ToggleTimelineButton">
                    <Button
                        type="primary"
                        onClick={() => dispatch(toggleRailInspectionTimelineWidget())}
                        icon="up">
                        Show Analytics
                    </Button>
                </div>
            )}

            <Drawer
                title={false}
                className="RailInspectionControlsTimelineDrawer"
                placement={"bottom"}
                closable={false}
                visible={timelineWidgetOpen}
                mask={false}>
                <SessionTimeline
                    inRailInspection={true}
                    formattedTime={formattedTime}
                />
            </Drawer>
        </div>
    );
};

export default RailInspectControls;
